import type { GroupviewPanelState } from 'dockview';
import { useCallback, useMemo } from 'react';
import type { LayoutPanelApi } from '../../components/AppLayout/types';
import type { PanelConfig } from '../../types/LayoutConfig';
import { useMobileLayoutPanel } from './MobileLayoutPanelContext';

/**
 * Adapts {@link MobileLayoutPanelContext.updatePanelParams} to the dockview
 * {@link LayoutPanelApi.updateParameters} shape used by panel-param storage hooks.
 */
export function useMobileLayoutPanelApi(panel: GroupviewPanelState): LayoutPanelApi {
  const { updatePanelParams } = useMobileLayoutPanel();

  const updateParameters = useCallback(
    (paramsPatch: Partial<PanelConfig>) => {
      if (paramsPatch == null || typeof paramsPatch !== 'object') {
        return;
      }
      updatePanelParams(paramsPatch);
    },
    [updatePanelParams]
  );

  const panelType = panel.params != null && 'type' in panel.params ? panel.params.type.toString() : '';

  return useMemo(
    () => ({
      updateParameters,
      close: () => undefined,
      id: panel.id,
      title: panel.title ?? '',
      component: panelType,
      setTitle: () => undefined,
      location: { type: 'grid' as const },
      onDidLocationChange: () => ({ dispose: () => undefined }),
    }),
    [panel.id, panel.title, panelType, updateParameters]
  );
}
